import "../../styles/global.css";
import "./Comparison.css"; // custom styles for this page
import React, { useState, useEffect, useMemo } from "react";
import { fetchQuestionAverageByStudent, fetchDemoQuestionAverageByStudent, 
         fetchClickSequenceByStudent, fetchDemoClickSequenceByStudent, fetchCurrentScoresByStudent} from "../../api/individualApi";
import { fetchQuestionAverage, fetchDemoQuestionAverage, 
         fetchAllSubmissionDates, fetchAllDemoSubmissionDates } from "../../api/overviewApi";
import { Slider, Typography, Select, MenuItem } from "@mui/material";
import dayjs from "dayjs";
import ClickBarChart from "./ClickBarChart";
import ScatterChart from "./ComparisonScatter"; 

// Mapping table
const Construct_code_category = [
  { construct: "Academic Burden", code: "bur", category: "Student Beliefs" },
  { construct: "Withdrawal Intention", code: "with", category: "Learning Behaviors" },
  { construct: "Self-Efficacy", code: "se", category: "Student Beliefs" },
  { construct: "Value", code: "val", category: "Student Beliefs" },
  { construct: "Intrinsic Motivation", code: "amot", category: "Student Beliefs" }, // or autonomous motivation? 
  { construct: "Extrinsic Motivation", code: "cmot", category: "Student Beliefs" }, // or controlled motivation?
  { construct: "Behavioral Engagement", code: "beh", category: "Learning Behaviors" },
  { construct: "Cognitive Engagement", code: "cog", category: "Learning Behaviors" },
  { construct: "Affective Engagement", code: "aff", category: "Learning Behaviors" },
  { construct: "Self-Regulation", code: "srt", category: "Learning Behaviors" },
  { construct: "Autonomy", code: "aut", category: "Classroom Context" },
  { construct: "Relatedness", code: "rel", category: "Classroom Context" },
  { construct: "Cultural Congruity", code: "cult", category: "Classroom Context" },
  { construct: "Belongingness", code: "bel", category: "Classroom Context" }, // or perceived belonging?  
];

const studentAliasMap = {
  "Zariel Galaviz": "Theon Greyjoy",
  "Liat Aberman": "Tyrion Lannister",
  "Kayden Jones": "Stannis Baratheon",
  "Alayna Smith": "Ygritte",
  "Britney Johnston": "Arya Stark",
  "Jaelinjoy Chavez": "Brienne Tarth",
  "Shawn Anthony Collado": "Jaime Lannister",
  "Lana Wilson": "Cersei Lannister",
  "Leya Dow": "Jon Snow",
  "Adrienne Bautista": "Sansa Stark",
  "Octavio Varela Segovia": "Eddard Stark",
};

const Comparison = () => {

    const [studentQuestionAverage, setStudentQuestionAverage] = useState([]);
    const [questionAverage, setQuestionAverage] = useState([]);
    const [error, setError] = useState(null);
    const [allDates, setAllDates] = useState([]);
    const [dateRange, setDateRange] = useState([null, null]);
    const [submissionRange, setSubmissionRange] = useState([null, null]);
    const [selectedCode, setSelectedCode] = useState("se")
    // const [clickSequence, setClickSequence] = useState({});
    const [studentClickStats, setStudentClickStats] = useState([]);
    const [selectedX, setSelectedX] = useState("se");
    const [selectedY, setSelectedY] = useState("cog");
    const [studentScores, setStudentScores] = useState([]); // array of { name, [code1]: val, [code2]: val }
    const [sessionInfo, setSessionInfo] = useState(null);
    const [currentScores, setCurrentScores] = useState([]);

    useEffect(() => {
        const cached = localStorage.getItem('iTOOLS_sessionInfo');
        if (cached) {
            console.log("Using cached session info");
            setSessionInfo(JSON.parse(cached));
        } else {
            console.log("No cached session info found");
            setSessionInfo({ name: "Demo Course" }); // Fallback to demo
        }
    }, []);

    useEffect(() => {
        if (!sessionInfo) return;
        console.log("Fetching all submission dates...");
        fetchAllSubmissionDates(sessionInfo?.courseId)
            .then((data) => {
                console.log("Comparison - Fetched all submission dates:", data);
                const raw = Array.isArray(data?.dates) ? data.dates : [];
                const ts = raw
                    .map((d) => Date.parse(d))
                    .filter((n) => Number.isFinite(n));
                // if (ts.length === 0) {
                //     // cold start: no submissions yet
                //     setAllDates([]);
                //     setDateRange([null, null]);
                //     return;
                // }
                const minDate = Math.min(...ts);
                const maxDate = Math.max(...ts);
                setSubmissionRange([minDate, maxDate]);
                

                const start_fa25 = Date.parse("2025-08-18T00:00:00-07:00");
                const end_now = Date.now();
                setDateRange([start_fa25, end_now]);
                setAllDates([start_fa25, end_now]);
            })
            .catch((error) => {
                console.error("Comparison - Error fetching all submission dates:", error);
                setAllDates([]);
                setDateRange([null, null]);
            });

        fetchCurrentScoresByStudent(sessionInfo?.courseId)
              .then((data) => {
                console.log("Comparison - Fetched current scores by student:", data.current_scores);
                setCurrentScores(data.current_scores || []);
              })
              .catch((error) => {
                console.error("Error fetching current scores by student:", error);
              });
    }, [sessionInfo]);

    useEffect(() => {
        if (dateRange[0] && dateRange[1]) {
            const [startISO, endISO] = dateRange.map((ts) => new Date(ts).toISOString());
            console.log("Comparison - Fetching data for date range:", startISO, endISO);
            fetchQuestionAverageByStudent(startISO, endISO, sessionInfo.courseId)
                .then((data) => setStudentQuestionAverage(data?.student_question_stats ?? [])) // not sure.. double check
                .catch(() => setError("Failed to load student averages"));

            fetchQuestionAverage(startISO, endISO, sessionInfo.courseId)
                .then((data) => setQuestionAverage(data.questions ?? []))
                .catch(() => setError("Failed to load class averages"))

            fetchClickSequenceByStudent(startISO, endISO, sessionInfo.courseId)
                .then((data) => setStudentClickStats(data?.click_sequence_stats ?? []))
                .catch(() => setError("Failed to load student click sequences"));


        }
    }, [dateRange, sessionInfo]);

    const filteredSortedData = useMemo(() => {
        // console.log("studentQuestionAverage", studentQuestionAverage)
        // console.log("selectedCode", selectedCode)
        return studentQuestionAverage
            .filter((d) => d.group_code === selectedCode)
            .sort((a, b) => a.avg_score - b.avg_score);
    }, [studentQuestionAverage, selectedCode])

    const pairedStudentScores = useMemo(() => {
        if (!selectedX || !selectedY || selectedX === selectedY) return [];
        const grouped = {};

        studentQuestionAverage.forEach(({ canvas_user_id, student_name, group_code, avg_score }) => {
            if (!grouped[canvas_user_id]) {
                grouped[canvas_user_id] = { canvas_user_id, name: student_name };
            }
            if (group_code === selectedX || group_code === selectedY) {
                grouped[canvas_user_id][group_code] = avg_score;
            }
        });

        return Object.values(grouped).filter(
            d => d[selectedX] !== undefined && d[selectedY] !== undefined
        );
    });

    console.log("questionAverage", questionAverage);

    const pairedAvgScores = useMemo(() => {
        if (!selectedX || !selectedY || selectedX === selectedY) return null;

        const xEntry = questionAverage.find(q => q.group_code === selectedX);
        const yEntry = questionAverage.find(q => q.group_code === selectedY);

        if (xEntry && yEntry) {
            return {
                [selectedX]: xEntry.avg_score,
                [selectedY]: yEntry.avg_score
            };
        }
        return null; // return null if either entry is missing
    }, [questionAverage, selectedX, selectedY]);

    console.log("pairedAvgScores", pairedAvgScores);


    const classMeanScore = useMemo(() => {
        console.log("questionAverage", questionAverage)
        const found = questionAverage.find((q) => q.group_code === selectedCode);
        return found ? parseFloat(found.avg_score) : null;
    }, [questionAverage, selectedCode])

    // const studentNameMap = {};
    // console.log("studentQuestionAverage", studentQuestionAverage);
    // studentQuestionAverage.forEach((d) => {
    //     studentNameMap[d.canvas_user_id] = d.student_name;
    // });

    const studentNameMap = {};
        currentScores.forEach((d) => {
            studentNameMap[d.canvas_user_id] = d.student_name;
    });

    // const labeledStats = Object.entries(studentClickStats).map(([id, stat]) => {
    //     const realName = studentNameMap[id] ?? "Unnamed";
        
    //     let alias;
    //     if (sessionInfo.courseId) { // real course
    //         alias = realName;
    //     } else { // demo course
    //         alias = studentAliasMap[realName] ?? realName;
    //     }               
 
    //     return {
    //         canvas_user_id: id,
    //         name: alias,
    //         ...stat
    //     };
    // });

    // console.log("studentNameMap", studentNameMap);
    // console.log("studentClickStats", studentClickStats); // missing
    const labeledStats = Object.entries(studentClickStats)
        .filter(([id]) => studentNameMap[id]) // Only keep those with a name
        .map(([id, stat]) => {
            const realName = studentNameMap[id];

            let alias;
            if (sessionInfo.courseId) {
            alias = realName;
            } else {
            alias = studentAliasMap[realName] ?? realName;
            }

            return {
            canvas_user_id: id,
            realName,
            name: alias,
            ...stat
            };
        });

    console.log("studentNameMap", studentNameMap);
    console.log("Comparison-studentClickStats", studentClickStats);
    console.log("Comparison-labeledStats:", labeledStats);



    // const handleCheckboxChange = (code) => {
    //     if ([selectedX, selectedY].includes(code)) {
    //         if (selectedX === code) setSelectedX("");
    //         else setSelectedY("");
    //     } else if (!selectedX) {
    //         setSelectedX(code);
    //     } else if (!selectedY) {
    //         setSelectedY(code);
    //     }
    // };

    const handleCheckboxChange = (code) => {
        const isAlreadySelected = [selectedX, selectedY].includes(code);

        if (isAlreadySelected) {
            // Uncheck
            if (code === selectedX) setSelectedX("");
            if (code === selectedY) setSelectedY("");
        } else {
            // Add new selection
            if (!selectedX) {
            setSelectedX(code);
            } else if (!selectedY) {
            setSelectedY(code);
            } else {
            // both already filled, do nothing or alert user
            }
        }
    };
    
    console.log("labeledStats", labeledStats);

    const selectedConstruct = Construct_code_category.find((c) => c.code === selectedCode);

    const handleSliderChange = (event, newValue) => setDateRange(newValue);

    // console.log("filteredSortedData", filteredSortedData)

    return (
        <div className="wrapper">
            <h1 className="title">COMPARISON OF STUDENT AVERAGES</h1>
            <div className="section-wrapper">
                <h2 className="section-title">1. Survey Data</h2>
                <p>Select a measure to show the rank of students from low to high. Drag the slider to adjust the time range of the surveys.</p>

                <div className="section-content">
                    {/* Left panel: controls */}
                    <div className="controls-panel">
                        <Typography variant="subtitle1" gutterBottom><strong>Select a measure:</strong></Typography>
                        <Select
                            value={selectedCode}
                            onChange={(e) => setSelectedCode(e.target.value)}
                            style={{ width: '100%', marginBottom: '2rem' }}
                        >
                            {Construct_code_category.map(({ construct, code}) => (
                                <MenuItem key={code} value={code}>{construct}</MenuItem>
                            ))}
                        </Select>

                        {Number.isFinite(allDates[0]) && Number.isFinite(allDates[1]) && 
                            Number.isFinite(dateRange[0]) && Number.isFinite(dateRange[1]) && (
                            <>
                                <Typography gutterBottom>Select time range of survey submissions and click data:</Typography>
                                <div className="slider-wrapper">
                                <Slider
                                    value={dateRange}
                                    onChange={handleSliderChange}
                                    valueLabelDisplay="auto"
                                    min={allDates[0]}
                                    max={allDates[1]}
                                    step={24 * 60 * 60 * 1000}
                                    valueLabelFormat={(ts) => dayjs(ts).format("MMM D")}
                                />
                                </div>
                            </>
                        )}
                    </div>

                    {/* Right panel: bar chart */}
                    <div className="bar-chart-wrapper">
                        <div className="label-column">
                            {filteredSortedData.map((d) => {
                                // <div key={d.user_id} className="bar-label">
                                // {studentAliasMap[d.student_name] || d.student_name}
                                // </div>
                                const realName = d.student_name;
                                const alias = sessionInfo?.courseId
                                ? realName
                                : (studentAliasMap[realName] ?? realName);

                                return (
                                <div key={d.user_id} className="bar-label">
                                    {alias}
                                </div>
                                );
                            })}
                        </div>
                        
                        <div className="bar-chart">
                            {classMeanScore && (
                                <>
                                <div
                                    className="class-mean-global-line"
                                    style={{ left: `${((classMeanScore - 1) / 5) * 100}%` }}
                                />
                                <div
                                    style={{
                                        position: "absolute",
                                        left: `${((classMeanScore - 1) / 5) * 100}%`,
                                        top: "-1.5rem",
                                        fontSize: "0.8rem",
                                        color: "#800080",
                                        transform: "translateX(-50%)"
                                    }}
                                    >
                                    {classMeanScore.toFixed(2)}
                                </div>
                                </>
                                
                            )}

                            {filteredSortedData.map((d) => (
                                <div key={d.user_id} className="bar-row">
                                    
                                    <div className="bar-container">
                                        <div
                                            className="bar-fill" 
                                            style={{ width: `${((parseFloat(d.avg_score) - 1) / 5) * 100}%` }} 
                                        /> 
                                        <div
                                            className="bar-dot"
                                            style={{ left: `${((parseFloat(d.avg_score) - 1) / 5) * 100}%` }}
                                        />
                                    </div>
                                    {/* <div className="bar-score">{parseFloat(d.avg_score).toFixed(2)}</div> */}
                                </div>
                            ))}

                            <div className="x-axis">
                                {[1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6].map((label) => (
                                    <div key={label} className="x-tick" style={{ left: `${((label - 1) / 5) * 100}%` }}>
                                        <div className="x-line" />
                                        <div className="x-label">
                                        {label === 1
                                            ? "Strongly Disagree"
                                            : label === 6
                                            ? "Strongly Agree"
                                            : label}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                
            </div>

            <div className="section-wrapper">
                <h2 className="section-title">2. WebCampus Click Data</h2>
                <p>Students' click behaviors up to this point are described by the following indicators:</p>
                <ul>
                    <li>
                        <strong>Breadth:</strong> The coverage of clicks on major WebCampus tools such as assignments, quizzes, discussions. A higher score means a <u>student has covered more course content</u>.
                    </li>
                    <li>
                        <strong>Depth:</strong> The density of clicks on major WebCampus tools such as assignments, quizzes, and discussions.
                        A higher score means a <u>student has clicked often on tools</u>.
                    </li>
                    <li>
                        <strong>Pattern:</strong> The sequential order of clicks on major WebCampus tools such as assignments, quizzes, and discussions.
                        A higher score means a <u>student navigates logically through course materials</u>.
                    </li>
                </ul>
                {labeledStats.length > 0 ? (
                    <ClickBarChart data={labeledStats} />
                ) : (
                    <p>No click data available.</p>
                )}
            </div>

            <div className="section-wrapper">
                <h2 className="section-title">3. Measure Comparison Matrix</h2>
                <p>Please choose any two measures to combine for comparison. This will allow you to search for students with combinations that may worthy of attention (e.g. high academic burden and low self efficacy). Hover over a dot to see the student's name and the actual scores. The red cross in the chart represents the class averages for the two selected measures. The scores are the average of all the survey responses up to this point.</p>
                
                <div className="section-content">
                    <div className="scatter-wrapper">
                        <ScatterChart
                            individual={pairedStudentScores}
                            avg={pairedAvgScores}
                            xKey={selectedX}
                            yKey={selectedY}
                        />
                    </div>
                    <div className="measure-checkboxes">
                        {Construct_code_category.map(({ construct, code }) => (
                            <label key={code} style={{ display: 'block', marginBottom: '0.5rem' }}>
                                <input
                                    type="checkbox"
                                    value={code}
                                    checked={[selectedX, selectedY].includes(code)}
                                    onChange={(e) => handleCheckboxChange(code)}
                                    disabled={
                                        ![selectedX, selectedY].includes(code) && selectedX && selectedY
                                    }
                                />
                                {' '}{construct}
                            </label>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Comparison;